import { EChartsOption } from 'echarts-for-react';
import { IChartStackedBarValue, DONUT_COLORS, IChartVisibilityValue } from '@/constants/geo';

import styles from './index.module.less';

const tooltipRound = (color: string) =>
  `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>`;

const COMMON_EXTRA_CSS_TEXT = `
border-radius: 12px;
border: 1px solid #FFF;
background: rgba(33, 43, 53, 0.40);
box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.10);
backdrop-filter: blur(5px);
color:  #FFF;
padding: 12px;
font-size: 12px ;
`;

// 环形图
export const getDonutOption = (config: { data: number[]; legend: string[]; colors?: string[] }) => {
  const { data, legend, colors = DONUT_COLORS } = config;
  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      formatter: (params: any) => `
        <div class="${styles['ctc_donut_wrap']}">
          <div class="${styles['ctc_donut_left']}">
            <span>${params.marker}</span>
            <div class="${styles['ctc_donut_left_name']}">${params.name}</div>
          </div>
          <div>${params.value}%</div>
        </div>`,
    },
    legend: {
      bottom: 0,
      left: 'center',
      data: legend,
      itemWidth: 10,
      itemHeight: 10,
      textStyle: {
        color: '#666',
        overflow: 'truncate',
        ellipsis: '...', // 超出部分显示省略号
        width: 90,
      },
      tooltip: { show: true },
    },
    series: [
      {
        type: 'pie',
        radius: ['50%', '70%'],
        center: ['50%', '35%'],
        avoidLabelOverlap: false,
        label: {
          show: false,
        },
        data: data.map((item, index) => ({
          value: item,
          name: legend[index],
          itemStyle: {
            color: colors[index],
          },
        })),
      },
    ],
  };
};

/** 获取平台可见性的 chart 配置 */
export const getVisibilityChartOption = (config: { legend: string[]; data: IChartVisibilityValue[]; isMobile?: boolean }): EChartsOption => {
  const { legend, data, isMobile } = config;

  const orangeGradient = {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: '#ffaf70' },
      { offset: 1, color: '#ff8c00' },
    ],
  };

  const visibilityBarOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'none',
      },
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      formatter: (params: any) => {
        const param = params[0];
        const value = param?.data?.value || 0;
        const realValue = param?.data?.realValue || 0;
        return `
        <div>
          <div class="${styles['ctc_bar_title']}">${param.name}</div>
          <div class="${styles['ctc_bar_wrap']}">
            <div class="${styles['ctc_bar_left']}">
              ${tooltipRound('#FA8846')}
              <div>${param.seriesName}</div>
            </div>
            <div>${realValue}%（${value}）</div>
          </div>
        </div>`;
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: legend,
      axisTick: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      ...(isMobile
        ? {
            axisLabel: {
              interval: 0, // 显示所有标签
              rotate: 45, // 旋转标签避免重叠
              overflow: 'break', // 文本超出时换行
            },
          }
        : {}),
    },
    yAxis: {
      type: 'value',
      // max: 100,
      axisLabel: {
        // formatter: '{value}%',
        fontFamily: 'DIN',
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
        },
      },
    },
    series: [
      {
        name: '可见性',
        type: 'bar',
        barWidth: '20px',
        data,
        itemStyle: {
          color: orangeGradient,
          opacity: 0.5,
          borderRadius: [5, 5, 0, 0],
        },
        // 悬停状态样式
        emphasis: {
          focus: 'series',
          itemStyle: {
            color: orangeGradient,
            opacity: 1,
          },
        },
      },
    ],
  };
  return visibilityBarOption;
};

// 堆叠柱状图
export const getStackedBarOption = (config: { legend: string[]; data: IChartStackedBarValue[][]; isMobile?: boolean }) => {
  const { legend, data, isMobile } = config;
  const radiusInfo: Record<string, number> = {};

  // 检查数据有效性
  if (!data || !data.length || !data[0] || !data[0].length) {
    return {
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: legend || [] },
      yAxis: { type: 'value' },
      series: []
    };
  }

  // x 轴循环
  for (let i = 0; i < data[0].length; ++i) {
    // y 轴循环
    for (let j = 0; j < data.length; ++j) {
      const info = data?.[j]?.[i];
      if (info?.value) {
        radiusInfo[i] = j;
      }
    }
  }

  const series = data.map((name, index) => {
    return {
      name: name,
      type: 'bar',
      stack: 'total',
      barWidth: '20px',
      data: data[index].map((item, i) => {
        const isHasRadius = radiusInfo[i] === index;
        return {
          ...item,
          itemStyle: {
            color: DONUT_COLORS[index],
            opacity: 0.5,
            borderRadius: [isHasRadius ? 5 : 0, isHasRadius ? 5 : 0, 0, 0],
          },
        };
      }),
      // 悬停状态样式
      emphasis: {
        focus: 'none',
        itemStyle: {
          opacity: 1,
        },
      },
    };
  });

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'none',
      },
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      // alwaysShowContent: true, // 关键配置：始终显示提示框
      formatter: (params: any) => {
        const param = params[0];
        let html = `
          <div>
            <div class="${styles['ctc_stacked_bar_title']}">${param.name}</div>
        `;

        params.forEach((el: any, index: number) => {
          const label = el?.data?.label || '';
          const value = el?.data?.value || 0;
          const realValue = el?.data?.realValue || 0;
          if (!label) return '';

          html += `
            <div class="${styles['ctc_stacked_bar_wrap']}">
              <div class="${styles['ctc_stacked_bar_left']}">
                ${tooltipRound(DONUT_COLORS[index])}
                <div class="${styles['ctc_stacked_bar_left_name']}">${label}</div>
              </div>
              <div>${realValue}%（${value}）</div>
            </div>
          `;
        });

        html += `
          </div>
        `;
        return html;
      },
    },
    legend: { show: false },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: legend,
      axisTick: { show: false },
      axisLine: { show: false },
      ...(isMobile
        ? {
            axisLabel: {
              interval: 0, // 显示所有标签
              rotate: 45, // 旋转标签避免重叠
              overflow: 'break', // 文本超出时换行
            },
          }
        : {}),
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        // formatter: '{value}%',
        fontFamily: 'DIN',
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
        },
      },
    },
    series,
  };
};
